// frontend/src/components/pages/chat/ToolsLanding.jsx
import React, { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";

// Import SVG assets for proper bundling
import transcriptifySvg from "/src/media/svg/transcriptify.svg";
import documindSvg from "/src/media/svg/documind.svg";
import codecraftSvg from "/src/media/svg/codecraft.svg";

const ToolsLanding = ({ onOpenTranscriptify }) => {
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  // Category tabs configuration
  const tabs = [
    { id: "all", label: "All type" },
    { id: "productivity", label: "Productivity" },
    { id: "education", label: "Education" },
    { id: "coding", label: "Coding" },
  ];

  // Tool categories mapping
  const toolCategories = {
    transcriptify: ["productivity", "education"],
    documind: ["productivity"],
    codecraft: ["coding", "productivity"],
  };

  // Filter tools based on active tab
  const getFilteredTools = () => {
    if (activeTab === "all") {
      return ["transcriptify", "documind", "codecraft"];
    }

    return Object.entries(toolCategories)
      .filter(([, categories]) => categories.includes(activeTab))
      .map(([tool]) => tool);
  };

  const filteredTools = getFilteredTools();

  const openTranscriptify = () => {
    try {
      const stored = localStorage.getItem("user");
      const token = stored ? JSON.parse(stored)?.token : undefined;
      const base =
        import.meta.env.VITE_TRANSCRIPTIFY_URL ||
        "http://localhost:5173/threads";
      const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
      window.location.href = url;
    } catch (_) {
      // Fallback to provided handler if parsing fails
      if (onOpenTranscriptify) onOpenTranscriptify();
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-6 py-8 custom-scrollbar">
      <div className="max-w-7xl mx-auto py-8 px-4 text-left">
        <h2 className="text-2xl font-semibold mb-2 text-fg-50">
          Explore our other tools...
        </h2>
        <p className="text-sm text-fg-60 mb-6">
          Discover powerful AI-driven tools designed to enhance your
          productivity and streamline your workflow.
        </p>

        {/* Search bar */}
        <div className="mb-6">
          <div className="flex items-center gap-2 px-3 py-3 rounded-md bg-bg-60">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
            >
              <g fill="none">
                <path
                  fill="currentColor"
                  fillOpacity=".16"
                  d="M11 19a8 8 0 1 0 0-16a8 8 0 0 0 0 16"
                />
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeMiterlimit="10"
                  strokeWidth="1.5"
                  d="m21 21l-4-4m2-6a8 8 0 1 1-16 0a8 8 0 0 1 16 0"
                />
              </g>
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools"
              className="flex-1 bg-transparent text-sm text-fg-50 placeholder:text-fg-60 focus:outline-none"
            />
          </div>
        </div>

        {/* Category tabs - styled like Usage.jsx */}
        <div className="mb-8">
          <div className="flex p-[2px] rounded-full bg-bg-50 max-w-fit">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  activeTab === tab.id
                    ? "bg-ac-02 text-white dark:text-black/70 shadow-sm"
                    : "text-fg-60 hover:text-fg-50 hover:bg-bg-40"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
          {/* Transcriptify Tool */}
          {filteredTools.includes("transcriptify") && (
            <div
              className="group relative bg-bg-70 hover:bg-bg-60 rounded-2xl border border-bd-50 p-6 transition-all duration-200 cursor-pointer"
              onClick={openTranscriptify}
            >
              <div className="absolute top-4 right-4">
                <ArrowUpRight
                  size={16}
                  className="text-fg-60 group-hover:text-fg-50"
                />
              </div>
              <div className="mb-4">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <img
                    src={transcriptifySvg}
                    alt="Transcriptify"
                    className="w-10 h-10"
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-fg-50">
                    Transcriptify
                  </h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                    New tool
                  </span>
                </div>
                <p className="text-sm text-fg-60 leading-relaxed">
                  Generate transcriptions, summarization & quizes
                </p>
              </div>
            </div>
          )}

          {/* DocuMind Tool */}
          {filteredTools.includes("documind") && (
            <div className="group relative bg-bg-70 hover:bg-bg-60 rounded-2xl border border-bd-50 p-6 transition-all duration-200 cursor-pointer opacity-75">
              <div className="absolute top-4 right-4">
                <ArrowUpRight
                  size={16}
                  className="text-fg-60 group-hover:text-fg-50"
                />
              </div>
              <div className="mb-4">
                <div className="w-10 h-10 flex items-center justify-center mb-4">
                  <img src={documindSvg} alt="DocuMind" className="w-12 h-12" />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-fg-50">Documind</h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400">
                    Coming soon
                  </span>
                </div>
                <p className="text-sm text-fg-60 leading-relaxed">
                  AI-powered document analysis and insights
                </p>
              </div>
            </div>
          )}

          {/* CodeCraft Tool */}
          {filteredTools.includes("codecraft") && (
            <div className="group relative bg-bg-70 hover:bg-bg-60 rounded-2xl border border-bd-50 p-6 transition-all duration-200 cursor-pointer opacity-75">
              <div className="absolute top-4 right-4">
                <ArrowUpRight
                  size={16}
                  className="text-fg-60 group-hover:text-fg-50"
                />
              </div>
              <div className="mb-4">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <img
                    src={codecraftSvg}
                    alt="CodeCraft"
                    className="w-10 h-10"
                  />
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-fg-50">
                    Codecraft
                  </h3>
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400">
                    Coming soon
                  </span>
                </div>
                <p className="text-sm text-fg-60 leading-relaxed">
                  Intelligent code generation and optimization
                </p>
              </div>
            </div>
          )}

          {/* More Tools placeholder */}
          {filteredTools.length > 0 && (
            <div className="group relative bg-gray-50 dark:bg-bg-60 rounded-2xl border border-dashed border-bd-50 p-6 opacity-50">
              <div className="mb-4">
                <div className="w-10 h-10 rounded-full bg-bg-30 flex items-center justify-center mb-4">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center">
                    <Plus size={16} />
                  </div>
                </div>
                <div className="mb-2">
                  <h3 className="text-lg font-semibold text-fg-50">
                    More Tools
                  </h3>
                </div>
                <p className="text-sm text-fg-60 leading-relaxed">
                  Many more exciting tools coming your way...
                </p>
              </div>
            </div>
          )}

          {/* Show message when no tools match the filter */}
          {filteredTools.length === 0 && (
            <div className="col-span-full text-center py-12">
              <div className="text-fg-60 text-sm">
                No tools available in this category yet.
              </div>
              <div className="text-fg-70 text-xs mt-1">
                More tools coming soon!
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolsLanding;
